import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Button,
  Grid,
  TextField,
  MenuItem,
  Checkbox,
  FormControlLabel,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Select,
  Modal,
  IconButton,
  InputAdornment,
  Autocomplete,
  CircularProgress,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import AddIcon from "@mui/icons-material/Add";
import SearchIcon from "@mui/icons-material/Search";
import CancelOutlinedIcon from "@mui/icons-material/CancelOutlined";
import color from "../../../constants/color";
import { formatDate as sharedFormatDate } from "../../../utils/DateFormat";
import style from "./style";

const CommunicationCenterEntity = ({
  groups,
  countries,
  historyData,
  onSend,
  historyFilters,
  onFilterChange,
  onSearchUsers,
  onCreateGroup,
  onUpdateGroup,
}) => {
  // Admins can only broadcast to Individual User / All Users.
  // Groups + Country targeting is SuperAdmin-only.
  const isAdmin = localStorage.getItem("role") === "Admin";
  const [sendTo, setSendTo] = useState("individual");
  const [moduleFeature, setModuleFeature] = useState("");
  const [target, setTarget] = useState("");
  
  const [isEmail, setIsEmail] = useState(true);
  const [isSms, setIsSms] = useState(false);
  const [isWhatsapp, setIsWhatsapp] = useState(false);

  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [schedule, setSchedule] = useState("");
  const [isImportant, setIsImportant] = useState(false);

  // Group Creation Modal State
  const [openModal, setOpenModal] = useState(false);
  const [editGroupId, setEditGroupId] = useState(null);
  const [newGroupName, setNewGroupName] = useState("");
  const [newGroupDesc, setNewGroupDesc] = useState("");
  
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [selectedUsers, setSelectedUsers] = useState([]);

  // View Details Modal State
  const [viewDetailsModal, setViewDetailsModal] = useState(false);
  const [selectedHistoryItem, setSelectedHistoryItem] = useState(null);

  // Individual Target Search State
  const [targetSearchQuery, setTargetSearchQuery] = useState("");
  const [targetSearchResults, setTargetSearchResults] = useState([]);
  const [isTargetSearching, setIsTargetSearching] = useState(false);

  useEffect(() => {
    let active = true;
    if (!targetSearchQuery || sendTo !== "individual") {
      setTargetSearchResults([]);
      return undefined;
    }

    const timer = setTimeout(async () => {
      setIsTargetSearching(true);
      const results = await onSearchUsers(targetSearchQuery);
      if (active) {
        setTargetSearchResults(results);
      }
      setIsTargetSearching(false);
    }, 500);

    return () => {
      active = false;
      clearTimeout(timer);
    };
  }, [targetSearchQuery, sendTo, onSearchUsers]);

  useEffect(() => {
    let active = true;
    if (!searchQuery) {
      setSearchResults([]);
      return undefined;
    }

    const timer = setTimeout(async () => {
      setIsSearching(true);
      const results = await onSearchUsers(searchQuery);
      if (active) {
        setSearchResults(results);
      }
      setIsSearching(false);
    }, 500);

    return () => {
      active = false;
      clearTimeout(timer);
    };
  }, [searchQuery, onSearchUsers]);

  const getStatusStyle = (status) => {
    switch (status?.toLowerCase()) {
      case "delivered":
        return { ...style.statusChip, ...style.statusDelivered };
      case "scheduled":
      case "pending":
        return { ...style.statusChip, ...style.statusPending };
      case "failed":
        return { ...style.statusChip, ...style.statusFailed };
      default:
        return style.statusChip;
    }
  };

  const handleSendClick = async () => {
    let channels = [];
    if (isEmail) channels.push("email");
    if (isSms) channels.push("sms");
    if (isWhatsapp) channels.push("whatsapp");

    const payload = {
      channels,
      audienceType: sendTo,
      audience: sendTo === "all users" ? [] : [target],
      module: moduleFeature,
      subject,
      message,
      isImportant,
    };

    if (schedule) {
      payload.scheduledAt = new Date(schedule).toISOString();
    }

    const success = await onSend(payload);
    if (success) {
      setTarget("");
      setSubject("");
      setMessage("");
      setSchedule("");
    }
  };

  const handleGroupSubmit = async () => {
    const payload = {
      name: newGroupName,
      description: newGroupDesc,
      users: selectedUsers.map(u => u._id),
    };

    let success;
    if (editGroupId) {
      success = await onUpdateGroup(editGroupId, payload);
    } else {
      success = await onCreateGroup(payload);
    }

    if (success) {
      setOpenModal(false);
      setNewGroupName("");
      setNewGroupDesc("");
      setSelectedUsers([]);
      setSearchQuery("");
      setSearchResults([]);
      setEditGroupId(null);
    }
  };

  const handleOpenCreateModal = () => {
    setEditGroupId(null);
    setNewGroupName("");
    setNewGroupDesc("");
    setSelectedUsers([]);
    setOpenModal(true);
  };

  const handleManageGroup = (group) => {
    setNewGroupName(group.name || "");
    setNewGroupDesc(group.description || "");
    const mappedUsers = (group.users || []).map(u => {
      if (typeof u === "string") return { _id: u, name: `User ID: ${u}`, email: "-" };
      return u;
    });
    setSelectedUsers(mappedUsers);
    setEditGroupId(group._id);
    setOpenModal(true);
  };

  const handleSelectUser = (event, newValue) => {
    if (!newValue) return;
    if (!selectedUsers.find(u => u._id === newValue._id)) {
      setSelectedUsers([...selectedUsers, newValue]);
    }
    setSearchQuery("");
    setSearchResults([]);
  };

  const handleRemoveUser = (userId) => {
    setSelectedUsers(selectedUsers.filter(u => u._id !== userId));
  };

  const formatDate = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    const timePart = date.toLocaleTimeString("en-GB", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
    return `${sharedFormatDate(dateString)} ${timePart}`;
  };

  return (
    <Box sx={style.container}>
      {/* Header */}
      <Box sx={style.headerContainer}>
        <Typography sx={style.pageTitle}>Communication Center</Typography>
        <Button
          startIcon={<AddIcon />}
          sx={style.createGroupBtn}
          onClick={handleOpenCreateModal}
        >
          Create User Group
        </Button>
      </Box>

      {/* Target Selection Card */}
      <Box sx={style.card}>
        <Grid container spacing={3}>
          <Grid item xs={12} md={4}>
            <Typography sx={style.inputLabel}>Send To</Typography>
            <Select
              fullWidth
              value={sendTo}
              onChange={(e) => {
                setSendTo(e.target.value);
                setTarget("");
                setTargetSearchQuery("");
                setTargetSearchResults([]);
              }}
              sx={style.textField}
              size="small"
            >
              <MenuItem value="individual">Individual User</MenuItem>
              {!isAdmin && <MenuItem value="groups">Groups</MenuItem>}
              {!isAdmin && <MenuItem value="country">Country</MenuItem>}
              <MenuItem value="all users">All Users</MenuItem>
            </Select>
          </Grid>
          <Grid item xs={12} md={4}>
            <Typography sx={style.inputLabel}>Select Target</Typography>
            {sendTo === "groups" ? (
              <Select
                fullWidth
                value={target}
                onChange={(e) => setTarget(e.target.value)}
                sx={style.textField}
                size="small"
                displayEmpty
              >
                <MenuItem value="" disabled>Select Group</MenuItem>
                {groups?.map(g => (
                  <MenuItem key={g._id} value={g._id}>{g.name}</MenuItem>
                ))}
              </Select>
            ) : sendTo === "country" ? (
              <Select
                fullWidth
                value={target}
                onChange={(e) => setTarget(e.target.value)}
                sx={style.textField}
                size="small"
                displayEmpty
              >
                <MenuItem value="" disabled>Select Country</MenuItem>
                {(Array.isArray(countries) ? countries : []).map((c, idx) => (
                  <MenuItem key={idx} value={c?.country || c}>{c?.country || c}</MenuItem>
                ))}
              </Select>
            ) : sendTo === "individual" ? (
              <Autocomplete
                fullWidth
                size="small"
                filterOptions={(x) => x}
                options={targetSearchResults}
                getOptionLabel={(option) => {
                  if (typeof option === "string") return option;
                  return `${option.name} (${option.email})`;
                }}
                isOptionEqualToValue={(option, value) => option._id === value._id}
                onInputChange={(e, newInputValue) => {
                  setTargetSearchQuery(newInputValue);
                }}
                onChange={(event, newValue) => {
                  if (newValue) {
                    setTarget(newValue.email || newValue);
                  } else {
                    setTarget("");
                  }
                }}
                loading={isTargetSearching}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder="Search by name or email"
                    sx={style.textField}
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <React.Fragment>
                          {isTargetSearching ? <CircularProgress color="inherit" size={20} /> : null}
                          {params.InputProps.endAdornment}
                        </React.Fragment>
                      ),
                    }}
                  />
                )}
                renderOption={(props, option) => {
                  return (
                    <li {...props} key={option._id}>
                      {option.name} ({option.email})
                    </li>
                  );
                }}
              />
            ) : (
              <TextField
                fullWidth
                placeholder="All Users Selected"
                size="small"
                sx={style.textField}
                disabled
                value="All Users Selected"
              />
            )}
          </Grid>
          <Grid item xs={12} md={4}>
            <Typography sx={style.inputLabel}>Module / Feature (Optional)</Typography>
            <Select
              fullWidth
              value={moduleFeature}
              onChange={(e) => setModuleFeature(e.target.value)}
              displayEmpty
              sx={style.textField}
              size="small"
            >
              <MenuItem value="">
                <span style={{ color: "#aaa" }}>Select Module</span>
              </MenuItem>
              {/* <MenuItem value="HR Module">HR Module</MenuItem> */}
              <MenuItem value="Employees">Employees</MenuItem>
              <MenuItem value="Employee List">Employee List</MenuItem>
              <MenuItem value="Profile Changes">Profile Changes</MenuItem>
              <MenuItem value="Attendance">Attendance</MenuItem>
              <MenuItem value="Deduction">Deduction</MenuItem>
              <MenuItem value="Loan">Loan</MenuItem>
              <MenuItem value="Leave Management">Leave Management</MenuItem>
              <MenuItem value="Commission">Commission</MenuItem>
              <MenuItem value="Travel Claims">Travel Claims</MenuItem>
              <MenuItem value="Recruitment">Recruitment</MenuItem>
              <MenuItem value="Timesheet">Timesheet</MenuItem>
              <MenuItem value="Projects">Projects</MenuItem>
              <MenuItem value="Payroll Module">Payroll Module</MenuItem>
              <MenuItem value="Salary Management">Salary Management</MenuItem>
              <MenuItem value="Reports">Reports</MenuItem>
            </Select>
          </Grid>
        </Grid>
        <Box sx={style.checkboxGroup}>
          <FormControlLabel
            control={<Checkbox checked={isEmail} onChange={(e) => setIsEmail(e.target.checked)} />}
            label={<Typography sx={style.checkboxLabel}>EMAIL</Typography>}
          />
          <FormControlLabel
            control={<Checkbox checked={isSms} onChange={(e) => setIsSms(e.target.checked)} />}
            label={<Typography sx={style.checkboxLabel}>SMS</Typography>}
          />
          <FormControlLabel
            control={<Checkbox checked={isWhatsapp} onChange={(e) => setIsWhatsapp(e.target.checked)} />}
            label={<Typography sx={style.checkboxLabel}>WHATSAPP</Typography>}
          />
        </Box>
      </Box>

      {/* Message Card */}
      <Box sx={style.card}>
        <Typography sx={style.inputLabel}>Subject</Typography>
        <TextField
          fullWidth
          placeholder="Enter subject (for Email)"
          size="small"
          sx={{ ...style.textField, mb: 3 }}
          value={subject}
          onChange={(e) => setSubject(e.target.value)}
        />
        <Typography sx={style.inputLabel}>Message</Typography>
        <TextField
          fullWidth
          multiline
          rows={6}
          placeholder="Write your message here..."
          sx={{ ...style.textField, mb: 2 }}
          value={message}
          onChange={(e) => setMessage(e.target.value)}
        />
        <FormControlLabel
          control={<Checkbox checked={isImportant} onChange={(e) => setIsImportant(e.target.checked)} />}
          label={<Typography sx={{ fontWeight: "600" }}>Mark as Important</Typography>}
        />
      </Box>

    {/* Schedule & Action Card */}
      <Box sx={style.card}>
        <Grid container spacing={3} alignItems="flex-end">
          <Grid item xs={12} md={6}>
            <Typography sx={style.inputLabel}>Schedule</Typography>
            <TextField
              type="datetime-local"
              fullWidth
              size="small"
              sx={style.textField}
              InputLabelProps={{ shrink: true }}
              value={schedule}
              onChange={(e) => setSchedule(e.target.value)}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <Box sx={style.actionContainer}>
              <Box />
              <Box display="flex" gap={2} justifyContent="flex-end" width="100%">
                <Button sx={style.btnDraft} onClick={handleSendClick}>Schedule</Button>
                <Button sx={style.btnSend} onClick={handleSendClick}>
                  Send Now
                </Button>
              </Box>
            </Box>
          </Grid>
        </Grid>
      </Box>

      {/* Communication History Card */}
      <Box sx={style.card}>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: 2 }}>
          <Box>
            <Typography sx={style.sectionTitle}>Communication History</Typography>
            <Typography sx={style.sectionSubtitle}>
              Track previously sent emails, SMS and WhatsApp messages.
            </Typography>
          </Box>
          <Box display="flex" gap={2}>
            <Select 
              size="small" 
              value={historyFilters.channel} 
              onChange={(e) => onFilterChange("channel", e.target.value)}
              sx={{ ...style.textField, minWidth: 150 }}
            >
              <MenuItem value="all">All Channels</MenuItem>
              <MenuItem value="email">Email</MenuItem>
              <MenuItem value="sms">SMS</MenuItem>
              <MenuItem value="whatsapp">WhatsApp</MenuItem>
            </Select>
            <Select 
              size="small" 
              value={historyFilters.status} 
              onChange={(e) => onFilterChange("status", e.target.value)}
              sx={{ ...style.textField, minWidth: 130 }}
            >
              <MenuItem value="all">All Status</MenuItem>
              <MenuItem value="scheduled">Scheduled</MenuItem>
              <MenuItem value="pending">Pending</MenuItem>
              <MenuItem value="delivered">Delivered</MenuItem>
              <MenuItem value="failed">Failed</MenuItem>
            </Select>
          </Box>
        </Box>

        <TableContainer sx={style.tableContainer}>
          <Table>
            <TableHead sx={style.tableHead}>
              <TableRow>
                <TableCell>Date & Time</TableCell>
                <TableCell>Channel</TableCell>
                <TableCell>Audience</TableCell>
                <TableCell>Subject</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {historyData && historyData.length > 0 ? (
                historyData.map((row, index) => (
                  <TableRow key={row._id || index}>
                    <TableCell>{formatDate(row.sentAt || row.scheduledAt || row.createdAt)}</TableCell>
                    <TableCell sx={{ textTransform: 'capitalize' }}>{row.channel}</TableCell>
                    <TableCell>{row.audience?.join(", ")}</TableCell>
                    <TableCell>{row.subject || "-"}</TableCell>
                    <TableCell>
                      <Box sx={getStatusStyle(row.status)}>{row.status}</Box>
                    </TableCell>
                    <TableCell>
                      <Typography 
                        sx={style.linkAction} 
                        style={{ cursor: "pointer" }}
                        onClick={() => {
                          setSelectedHistoryItem(row);
                          setViewDetailsModal(true);
                        }}
                      >
                        View Details
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    No history found
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>

      {/* User Groups Section */}
      <Box sx={style.card}>
        <Typography sx={style.sectionTitle}>User Groups</Typography>
        <Typography sx={style.sectionSubtitle}>Create and manage communication groups.</Typography>
        <Grid container spacing={3}>
          {groups?.map(group => (
            <Grid item xs={12} md={4} key={group._id}>
              <Box sx={style.groupCard}>
                <Box>
                  <Box sx={style.groupHeader}>
                    <Typography sx={style.groupName}>{group.name}</Typography>
                    <Typography sx={style.groupCount}>{group.users?.length || 0} Users</Typography>
                  </Box>
                  <Typography sx={style.groupDesc}>
                    {group.description}
                  </Typography>
                </Box>
                <Typography sx={style.linkAction} onClick={() => handleManageGroup(group)} style={{ cursor: 'pointer' }}>Manage Group</Typography>
              </Box>
            </Grid>
          ))}
          <Grid item xs={12} md={4}>
            <Box sx={style.createGroupCard} onClick={handleOpenCreateModal}>
              <AddIcon sx={{ mb: 1 }} />
              <Typography sx={{ fontWeight: "600", color: "#333" }}>Create New Group</Typography>
              <Typography sx={{ fontSize: "12px", color: "#666" }}>
                Build custom communication audiences.
              </Typography>
            </Box>
          </Grid>
        </Grid>
      </Box>

      {/* Create / Update User Group Modal */}
      <Modal open={openModal} onClose={() => setOpenModal(false)}>
        <Box sx={style.modalBox}>
          <Box sx={style.modalHeader}>
            <Box>
              <Typography sx={style.modalTitle}>{editGroupId ? "Update Group" : "Create User Group"}</Typography>
              <Typography sx={style.modalDesc}>
                {editGroupId ? "Update your communication audience." : "Create a reusable audience for communication campaigns."}
              </Typography>
            </Box>
            <IconButton onClick={() => setOpenModal(false)}>
              <CloseIcon />
            </IconButton>
          </Box>

          <Typography sx={style.inputLabel}>Group Name</Typography>
          <TextField
            fullWidth
            placeholder="e.g. HR Managers"
            size="small"
            sx={{ ...style.textField, mb: 3 }}
            value={newGroupName}
            onChange={(e) => setNewGroupName(e.target.value)}
          />

          <Typography sx={style.inputLabel}>Description</Typography>
          <TextField
            fullWidth
            multiline
            rows={3}
            placeholder="Describe the purpose of this user group"
            sx={{ ...style.textField, mb: 3 }}
            value={newGroupDesc}
            onChange={(e) => setNewGroupDesc(e.target.value)}
          />

          <Typography sx={style.inputLabel}>Add Users</Typography>
          <Autocomplete
            fullWidth
            size="small"
            filterOptions={(x) => x}
            options={searchResults}
            getOptionLabel={(option) => `${option.name} (${option.email})`}
            isOptionEqualToValue={(option, value) => option._id === value._id}
            onInputChange={(e, newInputValue) => {
              setSearchQuery(newInputValue);
            }}
            onChange={handleSelectUser}
            loading={isSearching}
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder="Search by name or email"
                sx={style.textField}
                InputProps={{
                  ...params.InputProps,
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <React.Fragment>
                      {isSearching ? <CircularProgress color="inherit" size={20} /> : null}
                      {params.InputProps.endAdornment}
                    </React.Fragment>
                  ),
                }}
              />
            )}
            renderOption={(props, option) => {
              return (
                <li {...props} key={option._id}>
                  {option.name} ({option.email})
                </li>
              );
            }}
          />

          <Box sx={style.selectedUsersBox}>
            <Box sx={style.selectedUsersHeader}>
              <Typography sx={{ fontWeight: "600" }}>Selected Users</Typography>
              <Typography sx={style.groupCount}>{selectedUsers.length} Selected</Typography>
            </Box>
            <TableContainer>
              <Table size="small">
                <TableHead sx={{ "& .MuiTableCell-root": { color: color.primary, borderBottom: "none", fontWeight: "600" } }}>
                  <TableRow>
                    <TableCell>Name</TableCell>
                    <TableCell>Email</TableCell>
                    <TableCell>Phone</TableCell>
                    <TableCell>Country</TableCell>
                    <TableCell align="right"></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody sx={{ "& .MuiTableCell-root": { borderBottom: "none", color: "#666" } }}>
                  {selectedUsers.length > 0 ? (
                    selectedUsers.map((user) => (
                      <TableRow key={user._id}>
                        <TableCell>{user.name || "-"}</TableCell>
                        <TableCell>{user.email || "-"}</TableCell>
                        <TableCell>{user.phone || "-"}</TableCell>
                        <TableCell>{user.address || "-"}</TableCell>
                        <TableCell align="right">
                          <IconButton size="small" sx={{ color: color.primary }} onClick={() => handleRemoveUser(user._id)}>
                            <CancelOutlinedIcon />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={5} align="center" sx={{ py: 3 }}>
                        <Typography sx={{ color: "#999", fontSize: "14px" }}>No users selected yet.</Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>

          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3, gap: 2 }}>
            <Button onClick={() => setOpenModal(false)} sx={{ color: '#666', textTransform: 'none' }}>
              Cancel
            </Button>
            <Button 
              variant="contained" 
              onClick={handleGroupSubmit} 
              sx={style.btnSend}
              disabled={!newGroupName || selectedUsers.length === 0}
            >
              {editGroupId ? "Update Group" : "Create Group"}
            </Button>
          </Box>
        </Box>
      </Modal>

      {/* View Details Modal */}
      <Modal open={viewDetailsModal} onClose={() => setViewDetailsModal(false)}>
        <Box sx={style.modalBox}>
          <Box sx={style.modalHeader}>
            <Typography sx={style.modalTitle}>Communication Details</Typography>
            <IconButton onClick={() => setViewDetailsModal(false)}>
              <CloseIcon />
            </IconButton>
          </Box>
          <Box sx={{ mt: 2 }}>
            <Typography sx={{ fontWeight: '600', mb: 1, color: '#333' }}>Subject</Typography>
            <Typography sx={{ mb: 3, color: '#555' }}>{selectedHistoryItem?.subject || "-"}</Typography>
            <Typography sx={{ fontWeight: '600', mb: 1, color: '#333' }}>Message</Typography>
            <Typography sx={{ whiteSpace: 'pre-wrap', color: '#555' }}>
              {selectedHistoryItem?.message || "-"}
            </Typography>
          </Box>
        </Box>
      </Modal>
    </Box>
  );
};

export default CommunicationCenterEntity;
